import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { clearLocalStorages, getLocalStorage } from '@/utils/storages';
import { SERVICES } from '@/configs';
import { FeedbackFormData } from '@/types/Feedback';

type AdminParams = {
  filterRating?: string;
  filterNPS?: string;
  filterStatus?: string;
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string ;
};

export const handle401Error = (error: any) => {
  if (error?.response?.status === 401 || error?.response?.data?.code === 401) {
    clearLocalStorages();
    window.location.href = '/';
    return true;
  }
  return false;
};

export function useFeedback({ filterNPS, filterRating, filterStatus, page, pageSize, startDate, endDate
}: AdminParams = {
  page: 1,
  pageSize: 10
}) {
  const accessToken = getLocalStorage('access_token');

  return useQuery({
    queryKey: ['admin-list', filterNPS, filterRating, filterStatus, page, pageSize, startDate, endDate],
    queryFn: async () => {
      let res = await axios.get(SERVICES.FEEDBACK + '/dashboard', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          page,
          limit: pageSize,
          ...(filterNPS && filterNPS !== 'all' ? { score: filterNPS } : {}),
          ...(filterRating && filterRating !== 'all' ? { stars: filterRating } : {}),
          ...(filterStatus && filterStatus !== 'all' ? { reviewStatus: filterStatus } : {}),
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        }
      });

      return res?.data?.data;
    },
    enabled: !!accessToken,
  });
};


export function useDownloadFeedback({ filterNPS, filterRating, filterStatus, page, pageSize, startDate, endDate
}: AdminParams = {
  page: 1,
  pageSize: 10
}) {
  const accessToken = getLocalStorage('access_token');

  return useQuery({
    queryKey: ['admin-list', filterNPS, filterRating, filterStatus, page, pageSize, startDate, endDate],
    queryFn: async () => {
      let res = await axios.get(SERVICES.FEEDBACK + '/dashboard', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          page,
          limit: pageSize,
          ...(filterNPS && filterNPS !== 'all' ? { score: filterNPS } : {}),
          ...(filterRating && filterRating !== 'all' ? { stars: filterRating } : {}),
          ...(filterStatus && filterStatus !== 'all' ? { reviewStatus: filterStatus } : {}),
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        }
      });

      return res?.data?.data;
    },
    enabled: false,
  });
};

async function fetchEditFeedback(feedbackParams: FeedbackFormData): Promise<string> {
  const accessToken = getLocalStorage('access_token');

  try {
    const options = {
      method: 'PUT',
      url: SERVICES.FEEDBACK + '/review',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      data: feedbackParams,
    };

    const res = await axios(options);

    if (res?.data?.code === 200) {
      return res?.data?.code;
    }
    if (res?.data?.code === 401) {
      clearLocalStorages();
      window.location.href = '/';
      throw new Error('Unauthorized');
    }
    throw new Error(res?.data?.code);
  } catch (error: any) {
    if (handle401Error(error)) {
      throw new Error('Unauthorized');
    }
    throw error;
  }
}


export function useEditFeedback(feedbackParams: FeedbackFormData) {
  const queryClient = useQueryClient();
  const queryKey = ['edit-feedback'];

  const query = useQuery({
    queryKey,
    queryFn: async () => await fetchEditFeedback(feedbackParams),
    enabled: false,
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey });
    };
  }, [queryClient]);

  return query;
}